import type { MapView } from "../map/MapView";
import type { Playable } from "../game/derive";
import type { RoundResult } from "../game/share";
import type { Credits } from "../game/knowledge";
import type { Level } from "../puzzles/types";
import type { LatLng } from "../game/geo";

export type { Child } from "../ui/dom";
import type { Child } from "../ui/dom";

/** What the game gives each round module. */
export interface Host {
  map: MapView;
  /** Replace the panel: progress bar, kicker, title, then these children. */
  frame(p: Playable, ...kids: Child[]): void;
  /** Record the round: score (0–1000, clamped), share details, verdict headline, Knowledge Map credits by slot. */
  finish(score: number, extra: Omit<RoundResult, "type" | "score">, reveal: string, credits: Credits): void;
  /** Verdict box with explanation and the next-round button. */
  verdict(): HTMLElement;
  /** Re-render the current round. */
  draw(): void;
  /**
   * Run `step(t)` every frame for `ms`, t going 0 → 1 (map-only animation, e.g. the pin reveal). Instant (one call with
   * t = 1) with reduced motion; stops early when the round changes.
   */
  animate(ms: number, step: (t: number) => void): void;
  toast(msg: string): void;
  /** Polite screen-reader announcement (aria-live), e.g. a newly revealed place or wrong-guess feedback. */
  announce: (msg: string) => void;
  /** Called with the first pin of a Pin it round (the one-time zoom nudge). */
  firstPin?: (ll: LatLng) => void;
  /** The level being played (keys the round's live state in storage). */
  level: Level;
  /** Autocomplete names for "Whose steps?". */
  names: readonly string[];
  /** Deterministic shuffle seed for this day and round. */
  seed: number;
}

export interface RoundState {
  done: boolean;
}

export interface RoundModule<P extends Playable, S extends RoundState> {
  init(p: P, host: Host): S;
  /** Frame the map for this round (ms = animation length, 0 = jump). */
  view(host: Host, s: S, p: P, ms: number): void;
  render(host: Host, s: S, p: P): void;
}

export const LETTERS = "ABCDEFGH";

import type { Place } from "../puzzles/types";

/** After a round: tapping pin `id` opens a popup with that place's step note (Place.note). */
export function tapToExplain(host: Host, places: Record<number, Place>): void {
  host.map.onPinClick = (id) => {
    const p = places[id];
    if (p) host.map.showCallout({ lat: p.lat, lng: p.lng, title: p.name, sub: p.when ?? null, body: p.note ?? null });
  };
}
